import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, setEntity, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { LivrableCaractériséService } from '../livrable-caracterisé.service';
import { LivrableCaractérisé } from '../model/livrable-caracterisé.model';
import { LivrableCaractériséUpdateRequest } from '../requests/update-request';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesLivrablesCaractérisés<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<LivrableCaractérisé> & SelectedEntityState>(),
      props: type<EntityProps<LivrableCaractérisé>>(),
    },
    withMethods((store) => ({
      chargerLivrablesCaracterises: (state: { livrablesCaractérisés: LivrableCaractérisé[] }) =>
        patchState(store, setAllEntities(state.livrablesCaractérisés)),
      ajouterLivrableCaractérisé: (livrableCaractérisé: LivrableCaractérisé) =>
        patchState(store, setEntity(livrableCaractérisé)),
      modifierLivrableCaractérisé: (id: string, livrableCaractérisé: Partial<LivrableCaractérisé>) =>
        patchState(store, updateEntity({ id, changes: livrableCaractérisé })),
    })),
    withMethods((store, service = inject(LivrableCaractériséService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idModule) =>
            service.trouverTous(idModule).pipe(
              tapResponse({
                next: (result) => store.chargerLivrablesCaracterises({ livrablesCaractérisés: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des livrables caractérisés, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: LivrableCaractériséUpdateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierLivrableCaractérisé(id, { caracteristiques: data.caractéristiques });
                  next?.();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification des livrables caractérisés, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });

                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((idLivrableCaractérisé) => service.trouverParId(idLivrableCaractérisé)),
          tapResponse({
            next: (livrableCaractérisé) => {
              store.ajouterLivrableCaractérisé(livrableCaractérisé);
            },
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de l'ajout d'un livrable caractérisé, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
    })),
  );
}
